/**
 * API Endpoints Configuration
 *
 * Centralized API endpoint definitions to prevent double /api/ prefix regressions
 *
 * IMPORTANT: All endpoints are relative paths that work with api.ts baseURL
 * DO NOT add /api/ prefix here as it's already included in api.ts baseURL
 */

export const API_ENDPOINTS = {
  // AI Services - only supported extract-metadata endpoints
  AI: {
    EXTRACT_METADATA: '/ai/extract-metadata',
    EXTRACT_METADATA_SONG: '/ai/extract-metadata/song',
    EXTRACT_METADATA_LOOK: '/ai/extract-metadata/look',
    EXTRACT_METADATA_MOVE: '/ai/extract-metadata/move',
    EXTRACT_METADATA_WORLD: '/ai/extract-metadata/world',
    EXTRACT_METADATA_STAR: '/ai/extract-metadata/star',
  },

  // Asset Services - No /v1/ prefix (baseURL already includes /api/v1)
  ASSETS: {
    CREATE: '/assets',
    LIST: '/assets',
    GET: '/assets/:id',
    UPDATE: '/assets/:id',
    DELETE: '/assets/:id',
    COUNT: '/assets/count',
    TAGS: '/assets/:id/tags',
    SONG_SEARCH_REGISTRY: '/assets/song/search-registry',
    LAYER_SEARCH: '/assets/:layer/search',
  },

  // Auth Services - No /v1/ prefix (baseURL already includes /api/v1)
  AUTH: {
    GOOGLE: '/auth/google',
    INSTAGRAM: '/auth/instagram',
    TIKTOK: '/auth/tiktok',
    PROFILE: '/auth/profile',
    DEVICES: '/auth/devices',
    DEVICE_BY_INSTALLATION: '/auth/devices/:installationId',
    LINK_GOOGLE: '/auth/link/google',
    LINK_INSTAGRAM: '/auth/link/instagram',
    LINK_TIKTOK: '/auth/link/tiktok',
    LOGOUT_INSTAGRAM: '/auth/logout/instagram',
    LOGOUT_TIKTOK: '/auth/logout/tiktok',
    INSTAGRAM_CALLBACK: '/auth/instagram/callback',
  },

  // Taxonomy Services - No /v1/ prefix (baseURL already includes /api/v1)
  TAXONOMY: {
    LAYERS: '/taxonomy/layers',
    CATEGORIES: '/taxonomy/layers/:layerId/categories',
    SUBCATEGORIES:
      '/taxonomy/layers/:layerId/categories/:categoryId/subcategories',
  },

  VIDEO: {
    GENERATE: '/video/generate',
    GET: '/video/:id',
  },
} as const;

/**
 * URL Construction Helper
 *
 * Safely constructs URLs with parameter substitution
 * Prevents common URL construction errors
 */
export class EndpointBuilder {
  /**
   * Build endpoint URL with parameter substitution
   * @param endpoint - Endpoint template (e.g., '/assets/:id')
   * @param params - Parameters to substitute (e.g., { id: '123' })
   * @returns Constructed URL (e.g., '/assets/123')
   */
  static build(endpoint: string, params: Record<string, string> = {}): string {
    let url = endpoint;

    // Replace path parameters
    Object.entries(params).forEach(([key, value]) => {
      url = url.replace(`:${key}`, value);
    });

    // Validate no unreplaced parameters remain
    const unreplacedParams = url.match(/:[\w]+/g);
    if (unreplacedParams) {
      console.warn(
        `[EndpointBuilder] Unreplaced parameters found: ${unreplacedParams.join(
          ', '
        )}`
      );
    }

    return url;
  }

  /**
   * Validate endpoint format
   * Ensures endpoint follows the correct pattern (no /api/ prefix)
   * PREVENTS REGRESSION: Double /api/ prefix issue from Auto-Deploy #499 and #630
   */
  static validate(endpoint: string): boolean {
    // CRITICAL: Should not start with /api/ (prevents double prefix regression)
    if (endpoint.startsWith('/api/')) {
      console.error(
        `[EndpointBuilder] 🚨 REGRESSION DETECTED: endpoint "${endpoint}" starts with /api/`
      );
      console.error(
        `[EndpointBuilder] This causes double /api/ prefix when combined with baseURL`
      );
      console.error(
        `[EndpointBuilder] URLs become /api/api/... instead of /api/...`
      );
      console.error(
        `[EndpointBuilder] See REGRESSION_PREVENTION.md for details`
      );
      return false;
    }

    // Should start with /
    if (!endpoint.startsWith('/')) {
      console.error(
        `[EndpointBuilder] Invalid endpoint "${endpoint}" - should start with /`
      );
      return false;
    }

    // Additional check for double slashes
    if (endpoint.includes('//')) {
      console.error(
        `[EndpointBuilder] Invalid endpoint "${endpoint}" - contains double slashes`
      );
      return false;
    }

    return true;
  }
}

/**
 * Type-safe endpoint access
 */
export type AIEndpoints = typeof API_ENDPOINTS.AI;
export type AssetEndpoints = typeof API_ENDPOINTS.ASSETS;
export type AuthEndpoints = typeof API_ENDPOINTS.AUTH;
export type TaxonomyEndpoints = typeof API_ENDPOINTS.TAXONOMY;
export type VideoEndpoints = typeof API_ENDPOINTS.VIDEO;

/**
 * Runtime validation of all endpoints
 * PREVENTS REGRESSION: Double /api/ prefix issue from Auto-Deploy #499 and #630
 * This will catch configuration errors at startup
 */
function validateAllEndpoints() {
  console.log(
    '[API Endpoints] 🔍 Starting endpoint validation to prevent URL regressions...'
  );

  const allEndpoints: string[] = [];
  const endpointsByCategory: Record<string, string[]> = {};

  // Collect all endpoint values by category
  Object.entries(API_ENDPOINTS).forEach(([categoryName, category]) => {
    endpointsByCategory[categoryName] = [];

    // Handle both nested objects and direct string values
    if (typeof category === 'string') {
      // Direct string endpoint (like HEALTH: '/health')
      allEndpoints.push(category);
      endpointsByCategory[categoryName].push(`${categoryName}: ${category}`);
    } else {
      // Nested object with multiple endpoints (like AI: { GENERATE_METADATA: '...' })
      Object.entries(category).forEach(([endpointName, endpoint]) => {
        allEndpoints.push(endpoint);
        endpointsByCategory[categoryName].push(`${endpointName}: ${endpoint}`);
      });
    }
  });

  // Log all endpoints being validated
  console.log('[API Endpoints] 📋 Validating endpoints by category:');
  Object.entries(endpointsByCategory).forEach(([category, endpoints]) => {
    console.log(`  ${category}:`, endpoints);
  });

  // Validate each endpoint
  const invalidEndpoints = allEndpoints.filter(
    endpoint => !EndpointBuilder.validate(endpoint)
  );

  if (invalidEndpoints.length > 0) {
    console.error(
      '[API Endpoints] ❌ VALIDATION FAILED - Invalid endpoints detected:',
      invalidEndpoints
    );
    console.error(
      '[API Endpoints] This prevents the double /api/ prefix regression'
    );
    console.error(
      '[API Endpoints] See REGRESSION_PREVENTION.md for historical context'
    );
    throw new Error(
      `Invalid API endpoint configuration: ${invalidEndpoints.join(', ')}`
    );
  }

  console.log(
    `[API Endpoints] ✅ Validated ${allEndpoints.length} endpoints successfully`
  );
  console.log('[API Endpoints] 🛡️ No double /api/ prefix regression detected');
}

// Validate endpoints on module load
validateAllEndpoints();

export default API_ENDPOINTS;
